import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  SkeletonBodyText,
  Spinner,
  Text,
} from "@shopify/polaris";
import { type QueryKey, useQuery } from "@tanstack/react-query";
import type { CSSProperties, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type {
  PlanPreview,
  PlanPreviewCell,
  PlanPreviewDataset,
} from "../api/plan_previews";
import { usePlan } from "../hooks/usePlan";
import { type Feature, requiredPlanLabel } from "../lib/entitlements";
import { t } from "../lib/i18n";
import { QueryErrorBanner } from "./QueryErrorBanner";

export interface PlanDataPreviewColumn {
  key: string;
  label: string;
}

export interface PlanDataPreviewSection {
  dataset: PlanPreviewDataset;
  title: string;
  columns: readonly PlanDataPreviewColumn[];
}

interface Props {
  feature: Feature;
  featureName: string;
  previewQueryKey: QueryKey;
  previewQueryFn: () => Promise<PlanPreview[]>;
  sections: readonly PlanDataPreviewSection[];
  children: ReactNode;
}

const VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const WIDE_COLUMNS = new Set(["product", "supplier", "kit"]);
const IDENTIFIER_COLUMNS = new Set(["sku"]);

function displayPlanLabel(planCode: string | null, fallback: string): string {
  if (!planCode) return fallback;
  return planCode.charAt(0).toUpperCase() + planCode.slice(1);
}

function fixedCellWidth(columnKey: string): string {
  if (WIDE_COLUMNS.has(columnKey)) return "76%";
  if (IDENTIFIER_COLUMNS.has(columnKey)) return "58%";
  return "44%";
}

function rowVisualStyle(isSilhouette: boolean): CSSProperties {
  return {
    display: "grid",
    gap: "var(--p-space-300, 12px)",
    alignItems: "center",
    minHeight: 46,
    padding: "var(--p-space-300, 12px) var(--p-space-400, 16px)",
    borderTop: "1px solid var(--p-color-border-secondary, #e1e3e5)",
    opacity: isSilhouette ? 0.48 : 0.94,
    filter: `blur(${isSilhouette ? "2.40" : "0.35"}px)`,
  };
}

function PreviewCell({
  cell,
  columnKey,
}: {
  cell: PlanPreviewCell;
  columnKey: string;
}) {
  if (cell.tier === "visible" || cell.tier === "partial_1" || cell.tier === "partial_2") {
    return (
      <span
        data-plan-preview-cell-tier={cell.tier}
        style={{
          display: "inline-block",
          maxWidth: "100%",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          fontWeight: 600,
        }}
      >
        {cell.displayText}
      </span>
    );
  }

  return (
    <span
      data-plan-preview-cell-tier="masked"
      style={{
        display: "block",
        width: fixedCellWidth(columnKey),
        minWidth: 28,
        height: 11,
        borderRadius: 999,
        background: "var(--p-color-bg-fill-tertiary, #dfe3e8)",
      }}
    />
  );
}

function SilhouetteCell({ columnKey }: { columnKey: string }) {
  return (
    <span
      data-plan-preview-cell-tier="silhouette"
      style={{
        display: "block",
        width: fixedCellWidth(columnKey),
        minWidth: 28,
        height: 11,
        borderRadius: 999,
        background: "var(--p-color-bg-fill-tertiary, #dfe3e8)",
      }}
    />
  );
}

function LockedSection({
  section,
  preview,
}: {
  section: PlanDataPreviewSection;
  preview: PlanPreview;
}) {
  const columns = section.columns;
  const gridTemplateColumns = `repeat(${Math.max(columns.length, 1)}, minmax(94px, 1fr))`;
  const firstRow = preview.rows[0] ?? null;

  return (
    <Card padding="0">
      <div
        data-plan-preview-table={section.dataset}
        style={{ minWidth: Math.max(480, columns.length * 112) }}
      >
        <div style={{ padding: "var(--p-space-400, 16px)" }}>
          <Text as="h3" variant="headingSm">
            {section.title}
          </Text>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns,
            gap: "var(--p-space-300, 12px)",
            padding: "var(--p-space-300, 12px) var(--p-space-400, 16px)",
            background: "var(--p-color-bg-surface-secondary, #f6f6f7)",
          }}
        >
          {columns.map((column) => (
            <Text as="span" key={column.key} variant="bodySm" fontWeight="semibold">
              {column.label}
            </Text>
          ))}
        </div>
        <div style={{ position: "relative" }}>
          {firstRow && (
            <div
              data-plan-preview-row
              data-plan-preview-real-row
              style={{
                ...rowVisualStyle(false),
                gridTemplateColumns,
              }}
            >
              {columns.map((column, columnIndex) => (
                <PreviewCell
                  key={column.key}
                  cell={firstRow.cells[columnIndex] ?? {
                    tier: "empty",
                    displayText: null,
                  }}
                  columnKey={column.key}
                />
              ))}
            </div>
          )}
          {preview.count > 1 && (
            <div
              data-plan-preview-row
              data-plan-preview-silhouette-row
              style={{
                ...rowVisualStyle(true),
                gridTemplateColumns,
              }}
            >
              {columns.map((column) => (
                <SilhouetteCell key={column.key} columnKey={column.key} />
              ))}
            </div>
          )}
          {preview.count > 0 && (
            <div
              aria-hidden="true"
              data-plan-preview-gradient
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background:
                  "linear-gradient(to bottom, color-mix(in srgb, var(--p-color-bg-surface, #fff) 14%, transparent) 0%, color-mix(in srgb, var(--p-color-bg-surface, #fff) 32%, transparent) 42%, color-mix(in srgb, var(--p-color-bg-surface, #fff) 82%, transparent) 78%, var(--p-color-bg-surface, #fff) 100%)",
              }}
            />
          )}
        </div>
      </div>
    </Card>
  );
}

function validateBundle(
  sections: readonly PlanDataPreviewSection[],
  previews: PlanPreview[] | undefined,
): Map<PlanPreviewDataset, PlanPreview> | null {
  if (!previews || previews.length !== sections.length) return null;
  const byDataset = new Map(previews.map((preview) => [preview.dataset, preview]));
  if (byDataset.size !== previews.length) return null;
  for (const section of sections) {
    const preview = byDataset.get(section.dataset);
    if (!preview) return null;
    const expectedKeys = section.columns.map((column) => column.key);
    if (
      expectedKeys.length !== preview.columnKeys.length ||
      !expectedKeys.every((key, index) => key === preview.columnKeys[index])
    ) {
      return null;
    }
  }
  return byDataset;
}

function PreviewOverlay({
  featureName,
  planLabel,
  sections,
  previews,
  isLoading,
  isError,
  onRetry,
}: {
  featureName: string;
  planLabel: string;
  sections: readonly PlanDataPreviewSection[];
  previews: Map<PlanPreviewDataset, PlanPreview> | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      role="region"
      aria-label={t("{name}のプレビュー", { name: featureName })}
      style={{
        position: "absolute",
        insetInline: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        padding: "var(--p-space-400, 16px)",
        zIndex: 1,
      }}
    >
      <div
        data-plan-preview-overlay-card
        style={{
          width: "min(100%, 440px)",
          padding: "var(--p-space-400, 16px)",
          borderRadius: "var(--p-border-radius-300, 12px)",
          background: "var(--p-color-bg-surface, #fff)",
          border: "1px solid var(--p-color-border-secondary, #dfe3e8)",
          boxShadow: "var(--p-shadow-400, 0 4px 18px rgba(0, 0, 0, 0.14))",
        }}
      >
        <BlockStack gap="200" inlineAlign="center">
          <InlineStack gap="200" blockAlign="center" align="center">
            <span aria-hidden="true">🔒</span>
            <Badge tone="attention">{planLabel}</Badge>
          </InlineStack>
          <Text as="h2" variant="headingMd" alignment="center">
            {featureName}
          </Text>

          {isLoading ? (
            <InlineStack gap="200" blockAlign="center" align="center">
              <Spinner size="small" accessibilityLabel={t("読み込み中")} />
              <Text as="span">{t("データ件数を確認しています")}</Text>
            </InlineStack>
          ) : isError || !previews ? (
            <BlockStack gap="200" inlineAlign="center">
              <Text as="p" tone="critical" alignment="center">
                {t("データ件数を確認できませんでした")}
              </Text>
              <Button onClick={onRetry}>{t("再試行")}</Button>
            </BlockStack>
          ) : (
            <BlockStack gap="100" inlineAlign="center">
              {sections.map((section) => {
                const preview = previews.get(section.dataset);
                return (
                  <Text as="p" key={section.dataset} alignment="center">
                    {t("{name}: {count}件", {
                      name: section.title,
                      count: preview?.count.toLocaleString() ?? "—",
                    })}
                  </Text>
                );
              })}
              <span style={VISUALLY_HIDDEN}>
                {sections
                  .map(
                    (section) =>
                      `${section.title}: ${section.columns.map((column) => column.label).join("、")}`,
                  )
                  .join("。")}
              </span>
            </BlockStack>
          )}

          <Button
            variant="primary"
            onClick={() => navigate("/plans")}
            accessibilityLabel={t("{name}を表示するためのプランを見る", {
              name: featureName,
            })}
          >
            {t("表示する")}
          </Button>
        </BlockStack>
      </div>
    </div>
  );
}

/**
 * Data-list plan gate. Denied plans mount only the server-redacted preview
 * request; privileged children (queries and forms included) never mount.
 */
export function PlanDataPreviewGate({
  feature,
  featureName,
  previewQueryKey,
  previewQueryFn,
  sections,
  children,
}: Props) {
  const plan = usePlan();
  const denied =
    !plan.isLoading &&
    !plan.isError &&
    (!plan.allows(feature) || plan.isProductLimitExceeded === true);
  const previewQuery = useQuery({
    queryKey: [
      ...previewQueryKey,
      "shop-generation",
      plan.shopGeneration ?? "unresolved",
    ],
    // Every production preview function calls apiFetch, whose active tenant
    // binding checks the shop both before sending and after receiving. The
    // generation also partitions this cache key, so a late old response cannot
    // populate the next shop's preview.
    queryFn: previewQueryFn,
    enabled:
      denied &&
      plan.shopId !== null &&
      plan.shopDomain !== null &&
      plan.shopGeneration !== null,
  });

  if (plan.isLoading) {
    return <Spinner accessibilityLabel={t("プラン情報を確認中")} />;
  }
  if (plan.isError) {
    return (
      <QueryErrorBanner
        title={t("プラン情報を取得できませんでした")}
        onRetry={plan.retry}
      />
    );
  }
  if (!denied) return <>{children}</>;

  const previews = validateBundle(sections, previewQuery.data);
  const invalidBundle = previewQuery.isSuccess && previews === null;

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        minHeight: 300,
        borderRadius: "var(--p-border-radius-300, 12px)",
      }}
    >
      <div
        data-plan-preview-visual
        aria-hidden="true"
        ref={(node) => node?.setAttribute("inert", "")}
        style={{
          display: "grid",
          gap: "var(--p-space-400, 16px)",
          pointerEvents: "none",
          userSelect: "none",
          overflow: "hidden",
          paddingBottom: 150,
        }}
      >
        {previews ? (
          sections.map((section) => (
            <LockedSection
              key={section.dataset}
              section={section}
              preview={previews.get(section.dataset)!}
            />
          ))
        ) : (
          <Card>
            <SkeletonBodyText lines={5} />
          </Card>
        )}
      </div>
      <PreviewOverlay
        featureName={featureName}
        planLabel={displayPlanLabel(
          plan.productLimitRequiredPlanCode,
          requiredPlanLabel(feature),
        )}
        sections={sections}
        previews={previews}
        isLoading={previewQuery.isLoading}
        isError={previewQuery.isError || invalidBundle}
        onRetry={() => void previewQuery.refetch()}
      />
    </div>
  );
}
